// API Base URL must be supplied by environment for local and production deploys.
import {
  recordApiFailure,
  reportClientError,
} from "./errorReporter.js";

export const API_BASE = import.meta.env.VITE_API_URL;
const TEST_COMMAND_TOKEN = import.meta.env.VITE_TEST_COMMAND_TOKEN;

// Simple in-memory cache for GET requests
const cache = new Map();
const inFlightRoomJoins = new Map();
const CACHE_TTL = 30000; // 30 seconds

const getCacheTtl = (endpoint) => {
  if (endpoint.includes("/replay")) {
    return 5 * 60 * 1000;
  }

  if (endpoint.startsWith("/leaderboard")) {
    return 60 * 1000;
  }

  if (endpoint.startsWith("/user/profile")) {
    return 2 * 60 * 1000;
  }

  return CACHE_TTL;
};

// Cache key generator
const getCacheKey = (endpoint, headers) => {
  const authHeader = headers?.Authorization || "";
  return `${endpoint}:${authHeader}`;
};

// Check if cache entry is valid
const isCacheValid = (entry) => {
  return entry && Date.now() - entry.timestamp < entry.ttl;
};

// API utility functions with performance optimizations
export const apiRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem("ai_escape_game_token");
  const isAuthRequest = endpoint.startsWith("/auth/");
  if (!API_BASE) {
    throw new Error("VITE_API_URL is not configured");
  }
  const fullUrl = `${API_BASE}${endpoint}`;

  const defaultOptions = {
    headers: {
      "Content-Type": "application/json",
      ...(token && !isAuthRequest && { Authorization: `Bearer ${token}` }),
      ...(TEST_COMMAND_TOKEN && {
        "X-LangProtect-Test-Token": TEST_COMMAND_TOKEN,
      }),
    },
  };

  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  const isSlowEndpoint =
    endpoint.startsWith("/rooms") || endpoint.startsWith("/game");
  const timeoutMs = isSlowEndpoint ? 90000 : 10000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const startedAt = performance.now();

  config.signal = controller.signal;

  try {
    const response = await fetch(fullUrl, config);
    clearTimeout(timeoutId);

    if (response.status >= 500) {
      const apiDetails = {
        endpoint,
        method: config.method || "GET",
        status: response.status,
        durationMs: Math.round(performance.now() - startedAt),
        requestId: response.headers.get("X-Request-ID"),
      };
      void response.clone().text().then((responseBody) => {
        const api = recordApiFailure({
          ...apiDetails,
          path: endpoint,
          response: responseBody,
        });
        reportClientError(`API returned HTTP ${response.status}`, {
          source: "api.response",
          api,
        });
      }).catch(() => {
        const api = recordApiFailure({ ...apiDetails, path: endpoint });
        reportClientError(`API returned HTTP ${response.status}`, {
          source: "api.response",
          api,
        });
      });
    }

    if (response.status === 401 && !isAuthRequest) {
      // Token expired or invalid
      localStorage.removeItem("ai_escape_game_token");
      localStorage.removeItem("ai_escape_game_user");
      localStorage.removeItem("ai_escape_game_refresh_token");
      cache.clear(); // Clear cache on auth failure
      window.location.href = "/login";
      throw new Error("Authentication required");
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      const api = recordApiFailure({
        method: config.method || "GET",
        path: endpoint,
        durationMs: Math.round(performance.now() - startedAt),
      });
      reportClientError("API request timed out", {
        source: "api.timeout",
        api,
        metadata: { endpoint, method: config.method || "GET", timeout_ms: timeoutMs },
      });
      throw new Error("Request timeout");
    }
    const api = recordApiFailure({
      method: config.method || "GET",
      path: endpoint,
      durationMs: Math.round(performance.now() - startedAt),
    });
    reportClientError(error, {
      source: "api.network",
      api,
      metadata: { endpoint, method: config.method || "GET" },
    });
    throw error;
  }
};

export const apiGet = async (endpoint, useCache = true, retries = 2) => {
  const token = localStorage.getItem("ai_escape_game_token");
  const cacheKey = getCacheKey(endpoint, {
    Authorization: token ? `Bearer ${token}` : undefined,
  });

  // Check cache first for GET requests
  if (useCache) {
    const cachedEntry = cache.get(cacheKey);
    if (isCacheValid(cachedEntry)) {
      return {
        ok: true,
        json: async () => cachedEntry.data,
        clone: () => ({
          json: async () => cachedEntry.data,
        }),
      };
    }
  }

  // Retry logic for failed requests
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await apiRequest(endpoint);

      // Cache successful GET responses
      if (useCache && response.ok) {
        try {
          const responseClone = response.clone();
          const data = await responseClone.json();
          cache.set(cacheKey, {
            data,
            timestamp: Date.now(),
            ttl: getCacheTtl(endpoint),
          });
        } catch {
          // Silently fail cache attempts
        }
      }

      return response;
    } catch (error) {
      lastError = error;

      // Don't retry auth errors or client errors
      if (
        error.message.includes("Authentication") ||
        error.message.includes("401")
      ) {
        throw error;
      }

      // Wait before retry (exponential backoff)
      if (attempt < retries) {
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, attempt) * 1000),
        );
      }
    }
  }

  throw lastError;
};

export const apiPost = (endpoint, data) => {
  const request = () => apiRequest(endpoint, {
    method: "POST",
    body: JSON.stringify(data),
  });

  // React Strict Mode intentionally re-runs effects in development. Coalesce
  // identical room joins so both callers receive their own readable Response
  // without sending two concurrent mutations to the backend.
  if (/^\/rooms\/[^/]+\/join$/.test(endpoint)) {
    const key = `${endpoint}:${JSON.stringify(data)}`;
    let pending = inFlightRoomJoins.get(key);
    if (!pending) {
      pending = request();
      inFlightRoomJoins.set(key, pending);
      const clearPending = () => {
        if (inFlightRoomJoins.get(key) === pending) {
          inFlightRoomJoins.delete(key);
        }
      };
      void pending.then(clearPending, clearPending);
    }
    return pending.then((response) => response.clone());
  }

  return request();
};

export const apiPut = (endpoint, data) =>
  apiRequest(endpoint, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const apiDelete = (endpoint) =>
  apiRequest(endpoint, {
    method: "DELETE",
  });

// Cache management utilities
export const clearCache = () => {
  cache.clear();
};

export const invalidateCache = (endpoint) => {
  const keys = Array.from(cache.keys()).filter((key) =>
    key.startsWith(endpoint),
  );
  keys.forEach((key) => cache.delete(key));
};

export const getCacheStats = () => {
  return {
    size: cache.size,
    entries: Array.from(cache.entries()).map(([key, value]) => ({
      key,
      age: Date.now() - value.timestamp,
      ttl: value.ttl,
      valid: isCacheValid(value),
    })),
  };
};
